interface SurveyQuestion {
  id: number
  text: string
  type: string
  isRequired?: boolean
  maxSelections?: number
  modelPath?: string
  responses?: Array<{
    id: number
    text: string
    nestedQuestions?: SurveyQuestion[]
  }>
  hideText?: boolean
}

/**
 * Validates an answer for a survey question
 * @param question - The survey question to validate against
 * @param value - The answer value to validate
 * @param skipValue - Optional skip value (for insuranceMembershipId questions)
 * @returns Error message string if validation fails, null if valid
 */
export const validateAnswer = (
  question: SurveyQuestion,
  value: any,
  skipValue?: boolean
): string | null => {
  // If skip is enabled and checked, no validation needed
  if (skipValue && question.type === 'insuranceMembershipId') {
    return null
  }

  // freeFormSkip questions don't need validation (they're informational)
  if (question.type === 'freeFormSkip') {
    return null
  }

  // Required validation
  if (question.isRequired) {
    if (value === undefined || value === null || value === '') {
      return 'This field is required'
    }
  }

  // Type-specific validation
  switch (question.type) {
    case 'text':
    case 'longText':
    case 'textarea':
      if (question.isRequired && (!value || String(value).trim() === '')) {
        return 'This field is required'
      }
      break

    case 'date':
      if (question.isRequired && !value) {
        return 'This field is required'
      }
      if (value) {
        // Validate date format (YYYY-MM-DD)
        // Trim whitespace before validation
        const dateValue = String(value).trim()
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/
        if (!dateRegex.test(dateValue)) {
          return 'Please enter a valid date'
        }
        // Additional validation: ensure it's a valid date
        const [year, month, day] = dateValue.split('-').map(Number)
        const date = new Date(year, month - 1, day)
        if (
          date.getFullYear() !== year ||
          date.getMonth() !== month - 1 ||
          date.getDate() !== day
        ) {
          return 'Please enter a valid date'
        }
      }
      break

    case 'singleSelect':
    case 'select':
    case 'dropdown':
      // Check for null/undefined specifically, not falsy (0 is a valid response ID)
      if (
        question.isRequired &&
        (value === null || value === undefined || value === '')
      ) {
        return 'Please select an option'
      }
      break

    case 'multiSelect':
      if (
        question.isRequired &&
        (!Array.isArray(value) || value.length === 0)
      ) {
        return 'Please select at least one option'
      }
      if (
        question.maxSelections &&
        Array.isArray(value) &&
        value.length > question.maxSelections
      ) {
        return `Please select no more than ${question.maxSelections} options`
      }
      break

    case 'boolean':
      // Boolean questions are always valid (they have a default value)
      // Only check required if explicitly needed
      if (question.isRequired && (value === undefined || value === null)) {
        return 'This field is required'
      }
      break

    case 'insuranceMembershipId':
      if (
        !skipValue &&
        question.isRequired &&
        (!value || String(value).trim() === '')
      ) {
        return 'This field is required'
      }
      break

    case 'heightPicker':
    case 'weightPicker':
      if (
        question.isRequired &&
        (!value || String(value).trim() === '' || Number(value) === 0)
      ) {
        return 'This field is required'
      }
      // Additional validation can be added here for height/weight ranges if needed
      break

    case 'custom':
      // Custom components handle their own validation
      // Only check required if explicitly needed
      if (question.isRequired && (!value || value === '')) {
        return 'This field is required'
      }
      break

    default:
      // For unknown question types, only check required
      if (question.isRequired && (!value || value === '')) {
        return 'This field is required'
      }
      break
  }

  return null
}
